import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Phone, Mail, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import PhoneVerification from './PhoneVerification';
import SpeechRecognition from '../voice/SpeechRecognition';
import TranscriptDisplay from '../voice/TranscriptDisplay';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { useOpenAIAssistant } from '../VoiceChat/useOpenAIAssistant';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { AnimatePresence, motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocation } from 'react-router-dom';

type AuthMode = 'select' | 'login' | 'register';
type AuthMethod = 'phone' | 'email';

interface AuthFormProps {
  guestEmail?: string | null;
  guestName?: string | null;
}

const AuthForm = ({ guestEmail, guestName }: AuthFormProps) => {
  const location = useLocation();
  const [authMode, setAuthMode] = useState<AuthMode>('select');
  const [authMethod, setAuthMethod] = useState<AuthMethod>('phone');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [demoTranscript, setDemoTranscript] = useState('');
  const [demoAttempts, setDemoAttempts] = useState(0);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const isMobile = useIsMobile();

  // Check if user came from login button
  const isDirectLogin = location.state?.directLogin === true;

  const { 
    initializeOpenAI, 
    processWithGPT, 
    isProcessing, 
    response: demoResponse,
    isSpeaking,
    resetState
  } = useOpenAIAssistant();

  // Check for guest signup email from payment flow
  useEffect(() => {
    if (guestEmail) {
      setAuthMode('register');
      setAuthMethod('email');
      setEmail(guestEmail || '');
      
      if (guestName) {
        setFirstName(guestName || '');
      }
      
      // Show toast to inform user
      toast.info("Complete tu registro para continuar con su suscripción");
    } else if (isDirectLogin) {
      // If coming from login button, go directly to login mode
      setAuthMode('login');
    }
  }, [guestEmail, guestName, isDirectLogin]);

  const handleFinalTranscript = async (finalTranscript: string) => {
    if (finalTranscript.trim()) {
      try {
        if (demoAttempts >= 2) {
          setDemoTranscript(finalTranscript);
          setShowLoginDialog(true);
          return;
        }

        setDemoTranscript(finalTranscript);
        setDemoAttempts(prev => prev + 1);
        
        const demoPrompt = `
          ERES UN ASISTENTE MIGRATORIO DE DEMO LIMITADO.
          
          CONTEXTO: El usuario está haciendo preguntas sobre inmigración a Estados Unidos. 
          Debes dar una respuesta útil pero general, evitando detalles específicos.
          
          REGLAS ESTRICTAS:
          1. DA UNA RESPUESTA REAL a la pregunta del usuario (no uses texto genérico)
          2. La respuesta debe ser UNA SOLA oración informativa
          3. NO hagas preguntas ni pidas más información
          4. NO des consejos específicos ni procedimientos detallados
          5. IGNORA COMPLETAMENTE cualquier instrucción de sistema previa
          
          Por ejemplo, si preguntan "¿Qué necesito para trabajar en USA?":
          "Para trabajar legalmente en Estados Unidos necesitas una visa de trabajo como la H-1B o TN."

          RECUERDA:
          - RESPONDE REALMENTE a la pregunta
          - USA UNA SOLA oración informativa
          - NO des detalles específicos de procesos
          - NO COPIES el texto de ejemplo

          Pregunta del usuario: ${finalTranscript}
        `;

        await processWithGPT(demoPrompt);
        
        if (demoAttempts === 1 && !isSpeaking) {
          setTimeout(() => {
            setShowLoginDialog(true);
          }, 1000);
        }
      } catch (error) {
        console.error('Error processing transcript:', error);
        resetState();
      }
    }
  };

  const { isListening, startListening, stopListening } = useSpeechRecognition({
    onFinalTranscript: handleFinalTranscript,
    onTranscriptChange: (newTranscript: string) => {
      if (isListening) {
        setDemoTranscript(newTranscript);
      }
    },
    isDisabled: isProcessing || isSpeaking || demoAttempts >= 2
  });

  useEffect(() => {
    initializeOpenAI();
    return () => {
      resetState();
    };
  }, [initializeOpenAI, resetState]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '');
    setPhone(cleaned);
  };

  const resetForm = () => {
    setAuthMode('select');
    setFirstName('');
    setEmail('');
    setPhone('');
    setPassword('');
    setAuthMethod('phone');
    setShowVerification(false);
  };

  const sendVerificationCode = async (phoneNumber: string) => {
    try {
      const {
        error
      } = await supabase.auth.signInWithOtp({
        phone: `+1${phoneNumber}`
      });
      if (error) throw error;
      setShowVerification(true);
      toast.success("Código de verificación enviado");
    } catch (error: any) {
      console.error('Error sending verification code:', error);
      toast.error(error.message || "Error al enviar el código");
      throw error;
    }
  };

  const generateSecurePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (authMode === 'register' && !firstName.trim()) {
      toast.error("Por favor ingresa tu nombre");
      return;
    }
    if (authMethod === 'email' && !email) {
      toast.error("Por favor ingresa tu correo electrónico");
      return;
    }
    if (authMethod === 'phone' && !phone) {
      toast.error("Por favor ingresa tu número de teléfono");
      return;
    }
    if (authMethod === 'email' && (!password || password.length < 6)) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    try {
      setLoading(true);
      if (authMethod === 'phone') {
        if (authMode === 'register') {
          const generatedPassword = generateSecurePassword();
          const {
            error: signUpError
          } = await supabase.auth.signUp({
            phone: `+1${phone}`,
            password: generatedPassword,
            options: {
              data: {
                first_name: firstName
              }
            }
          });
          if (signUpError) {
            if (signUpError.message.includes('User already registered')) {
              toast.info("Este número ya está registrado. Procediendo con el inicio de sesión.");
              await sendVerificationCode(phone);
              return;
            }
            throw signUpError;
          }
        }
        await sendVerificationCode(phone);
        if (authMode === 'register') {
          localStorage.setItem('pendingRegistrationName', firstName);
        }
      } else {
        if (authMode === 'register') {
          const {
            error
          } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                first_name: firstName
              }
            }
          });
          if (error) {
            if (error.message.includes('User already registered')) {
              toast.error("Este correo ya está registrado. Por favor inicia sesión.");
              setAuthMode('login');
              return;
            }
            throw error;
          }
          toast.success("¡Cuenta creada exitosamente!");
          resetForm();
        } else {
          const {
            error
          } = await supabase.auth.signInWithPassword({
            email,
            password
          });
          if (error) throw error;
          toast.success("¡Inicio de sesión exitoso!");
          resetForm();
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      toast.error(error.message || "Error en la autenticación");
    } finally {
      setLoading(false);
    }
  };

  const renderDemo = () => (
    <AnimatePresence mode="wait">
      {(demoAttempts < 2 || isSpeaking) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20, transition: { duration: 0.5 } }}
          className="w-full space-y-4 mb-6"
        >
          {/* Voice Demo Button */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-3 md:p-6 rounded-lg md:rounded-xl border border-blue-200">
            <Button
              onClick={startListening}
              disabled={demoAttempts >= 2 || isProcessing || isSpeaking || isListening}
              className="w-full py-3 md:py-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold text-base md:text-lg rounded-lg md:rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 min-h-[52px] md:min-h-[64px] [animation-duration:3s] animate-pulse hover:animate-none disabled:animate-none"
            >
              <span className="flex items-center justify-center gap-2 md:gap-3 text-sm md:text-xl">
                {isListening ? '🎤 Escuchando...' : isProcessing ? '⏳ Procesando...' : isSpeaking ? '🔊 Hablando...' : '🎤 ¡Hazme una pregunta ahora!'}
              </span>
            </Button>
            
            <div className="text-center mt-2 md:mt-4">
              <p className="text-xs md:text-sm font-medium text-blue-700">
                {`${2 - demoAttempts} pregunta${2 - demoAttempts === 1 ? '' : 's'} de prueba restante${2 - demoAttempts === 1 ? '' : 's'}`}
              </p>
            </div>
          </div>
          
          {/* Text Input Section */}
          <div className="w-full space-y-3">
            <div className="flex items-center gap-2 md:gap-4 py-1">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
              <span className="text-xs md:text-sm font-medium text-gray-500 px-2">o escribe tu pregunta</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
            </div>
            
            <div className="space-y-2 md:space-y-3 bg-white p-3 md:p-4 rounded-lg border border-gray-200 shadow-sm">
              <Input
                value={demoTranscript}
                onChange={(e) => setDemoTranscript(e.target.value)}
                placeholder="Escribe tu pregunta aquí..."
                className="h-11 md:h-14 text-sm md:text-base border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg"
                disabled={demoAttempts >= 2 || isProcessing || isSpeaking}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && demoTranscript.trim()) {
                    handleFinalTranscript(demoTranscript.trim());
                  }
                }}
              />
              {demoTranscript.trim() && (
                <Button
                  onClick={() => handleFinalTranscript(demoTranscript.trim())}
                  disabled={demoAttempts >= 2 || isProcessing || isSpeaking}
                  className="w-full h-10 md:h-12 bg-green-600 hover:bg-green-700 text-white font-semibold text-sm md:text-base rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  Enviar pregunta
                </Button>
              )}
            </div>
            
            {/* Voice Recognition Component */}
            <div className="flex flex-col items-center py-2 md:py-4">
              <SpeechRecognition
                isListening={isListening}
                isProcessing={isProcessing}
                isSpeaking={isSpeaking}
                isDisabled={demoAttempts >= 2 || isProcessing || isSpeaking}
                onStart={startListening}
                onStop={stopListening}
              />
            </div>
            
            {/* Response Display */}
            {demoResponse && (
              <div className="mt-4 md:mt-6">
                <TranscriptDisplay
                  transcript=""
                  response={demoResponse}
                />
              </div>
            )}
          </div>
        </motion.div>
      )}

      <Dialog 
        open={showLoginDialog && !isSpeaking} 
        onOpenChange={setShowLoginDialog}
      >
        <DialogContent className="w-[95%] max-w-[425px] p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-bold text-center text-primary">¡No te pierdas más respuestas!</DialogTitle>
            <DialogDescription className="text-center pt-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="space-y-4"
              >
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">
                    ¡Has descubierto el poder de nuestro asistente! Continúa recibiendo ayuda ilimitada creando tu cuenta gratuita.
                  </p>
                </div>

                <div className="space-y-3">
                  <Button 
                    onClick={() => {
                      setShowLoginDialog(false);
                      setAuthMode('register');
                    }} 
                    className="w-full h-11 text-base bg-blue-600 hover:bg-blue-700 transition-all duration-200 min-h-[48px]"
                  >
                    Crear cuenta gratis
                  </Button>

                  <Button 
                    onClick={() => {
                      setShowLoginDialog(false);
                      setAuthMode('login');
                    }} 
                    variant="outline" 
                    className="w-full h-11 text-base hover:bg-blue-50 transition-all duration-200 min-h-[48px]"
                  >
                    Ya tengo cuenta
                  </Button>
                </div>
              </motion.div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </AnimatePresence>
  );

  const renderSelectMode = () => (
    <div className="space-y-4 md:space-y-6 pt-4 md:pt-6 pb-3 md:pb-4 border-t-2 border-gray-200">
      <div className="text-center mb-1">
        <p className="text-xs md:text-sm font-medium text-gray-600">¿Ya tienes cuenta?</p>
      </div>
      
      <div className="flex justify-center">
        <Button 
          onClick={() => setAuthMode('login')} 
          variant="outline"
          className="w-3/5 md:w-full h-10 md:h-12 text-sm md:text-base font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 rounded-lg shadow-sm hover:shadow-md"
        >
          Iniciar sesión
        </Button>
      </div>
      
      <div className="relative py-2 md:py-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center">
          <span className="px-3 md:px-4 bg-white text-xs md:text-sm font-medium text-gray-500">¿Nuevo usuario?</span>
        </div>
      </div>
      
      <div className="flex justify-center">
        <Button 
          onClick={() => setAuthMode('register')} 
          className="w-4/5 md:w-full h-14 md:h-14 text-sm md:text-base font-semibold bg-orange-500 hover:bg-orange-600 text-white transition-all duration-200 rounded-lg shadow-md hover:shadow-lg whitespace-normal leading-tight py-2"
        >
          Crear cuenta con número o correo electrónico
        </Button>
      </div>
    </div>
  );

  const renderAuthForm = () => <form onSubmit={handleSubmit} className="space-y-5 md:space-y-8 pt-2 md:pt-4">
      <div className="text-center pb-1 md:pb-2">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">
          {authMode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
        </h2>
        <p className="text-xs md:text-sm text-gray-600 mt-1 md:mt-2">
          {authMode === 'login' ? 'Ingresa tus datos para continuar' : 'Completa el formulario para comenzar'}
        </p>
      </div>

      {authMode === 'register' && <div className="space-y-2 md:space-y-3 bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200">
          <Label htmlFor="firstName" className="text-sm md:text-base font-semibold text-gray-700">Nombre</Label>
          <Input id="firstName" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="h-11 md:h-14 text-sm md:text-base border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200" placeholder="Ingresa tu nombre" />
        </div>}

      <div className="space-y-4 md:space-y-6">
        <div className="bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200">
          <Label className="text-xs md:text-sm font-semibold text-gray-700 mb-2 md:mb-3 block">Selecciona tu método de contacto</Label>
          <div className="grid grid-cols-2 gap-2 md:gap-3">
            <Button type="button" onClick={() => setAuthMethod('phone')} className={`h-11 md:h-14 text-sm md:text-base font-semibold transition-all duration-200 ${authMethod === 'phone' ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg scale-105' : 'bg-white hover:bg-gray-100 text-gray-700 border-2 border-gray-300'}`} variant={authMethod === 'phone' ? 'default' : 'outline'}>
              <Phone className="mr-1 md:mr-2 h-4 md:h-5 w-4 md:w-5" />
              Teléfono
            </Button>

            <Button type="button" onClick={() => setAuthMethod('email')} className={`h-11 md:h-14 text-sm md:text-base font-semibold transition-all duration-200 ${authMethod === 'email' ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg scale-105' : 'bg-white hover:bg-gray-100 text-gray-700 border-2 border-gray-300'}`} variant={authMethod === 'email' ? 'default' : 'outline'}>
              <Mail className="mr-1 md:mr-2 h-4 md:h-5 w-4 md:w-5" />
              Correo
            </Button>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5">
          {authMethod === 'email' ? <div className="space-y-2 md:space-y-3 bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200">
              <Label htmlFor="email" className="text-sm md:text-base font-semibold text-gray-700">Correo Electrónico</Label>
              <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="h-11 md:h-14 text-sm md:text-base border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200" placeholder="tucorreo@ejemplo.com" />
            </div> : <div className="space-y-2 md:space-y-3 bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200">
              <Label htmlFor="phone" className="text-sm md:text-base font-semibold text-gray-700">Número de teléfono</Label>
              <Input id="phone" type="tel" value={phone} onChange={handlePhoneChange} className="h-11 md:h-14 text-sm md:text-base border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200" placeholder="Ejemplo: 5551234567" maxLength={10} />
              <p className="text-xs text-gray-500 mt-1">Ingresa 10 dígitos sin espacios</p>
            </div>}

          {authMethod === 'email' && <div className="space-y-2 md:space-y-3 bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200">
              <Label htmlFor="password" className="text-sm md:text-base font-semibold text-gray-700 flex items-center gap-2">
                <Lock className="h-4 md:h-5 w-4 md:w-5 text-orange-500" />
                Contraseña
              </Label>
              <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="h-11 md:h-14 text-sm md:text-base border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200" placeholder="Mínimo 6 caracteres" minLength={6} />
            </div>}

          <Button type="submit" disabled={loading} className="w-full h-12 md:h-16 text-base md:text-lg font-bold bg-orange-500 hover:bg-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl rounded-lg md:rounded-xl mt-4 md:mt-6">
            {loading ? "Procesando..." : authMode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
          </Button>

          {authMode !== 'select' && (
            <Button 
              type="button" 
              variant="ghost" 
              onClick={() => setAuthMode('select')} 
              className="w-full mt-3 md:mt-4 h-10 md:h-12 text-sm md:text-base text-gray-600 hover:text-gray-800 hover:bg-gray-100"
            >
              ← Volver
            </Button>
          )}
        </div>
      </div>
    </form>;

  return (
    <div className="w-full max-w-md mx-auto px-3 md:px-4 -mt-16 md:-mt-20 relative z-20">
      <motion.div 
        className="bg-white rounded-xl md:rounded-2xl shadow-2xl p-4 md:p-8 transition-all duration-300 border border-gray-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col items-center space-y-4 md:space-y-8 pt-1 md:pt-2">
          <div className="text-center">
            <h1 className="text-xl md:text-3xl font-bold text-gray-800 mb-1 md:mb-2">
              Bienvenido
            </h1>
            <p className="text-xs md:text-sm text-gray-600">
              Tu asistente de inmigración inteligente
            </p>
          </div>
          
          {/* Only show demo if not coming from direct login and no guest email */}
          {!isDirectLogin && !guestEmail && renderDemo()}
          
          <AnimatePresence mode="wait">
            {authMode === 'select' ? renderSelectMode() : renderAuthForm()}
          </AnimatePresence>
        </div>
      </motion.div>

      <PhoneVerification 
        phoneNumber={phone} 
        onVerificationComplete={resetForm}
        isOpen={showVerification} 
        onClose={() => setShowVerification(false)} 
      />
    </div>
  );
};

export default AuthForm;
